import React, { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Line, ComposedChart } from "recharts";
import { FileDown, TrendingUp } from "lucide-react";
import api from "@/lib/api";

function money(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

async function downloadBoardPack() {
  const res = await api.get("/api/dashboard/board-pack.pdf", { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = `board-pack-${new Date().toISOString().slice(0, 10)}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function StewardshipLedger() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/api/dashboard/ledger", { params: { months: 12 } }).then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-sm text-muted-foreground p-6">Loading ledger…</div>;

  const series = data.series || [];
  const lastFunds = series.length ? series[series.length - 1].available_funds : 0;
  const totalDon = series.reduce((s, m) => s + m.donations, 0);
  const totalExp = series.reduce((s, m) => s + m.expenses, 0);

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="stewardship-ledger">
      <div className="px-6 py-5 border-b border-border flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-primary" />
            <h3 className="serif text-lg text-primary">Stewardship Ledger</h3>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Donations received vs expenses paid — the closer the gap, the more responsibly funds are flowing.</p>
        </div>
        <div className="flex gap-3 items-start">
          <button onClick={downloadBoardPack} data-testid="board-pack-export"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background hover:bg-accent px-3 py-2 text-xs font-medium">
            <FileDown size={14} /> Export Board Pack PDF
          </button>
        <div className="flex gap-6 text-right">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Donations</div>
            <div className="serif text-xl text-emerald-700 dark:text-emerald-400">{money(totalDon)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Expenses</div>
            <div className="serif text-xl text-primary">{money(totalExp)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Available</div>
            <div className={`serif text-xl ${lastFunds >= 0 ? "text-foreground" : "text-rose-600"}`}>{money(lastFunds)}</div>
          </div>
        </div>
        </div>
      </div>
      <div className="p-4">
        {series.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground text-sm">No donations or expenses yet — record some to populate the ledger.</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={series} margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
              <defs>
                <linearGradient id="gDon" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(207 70% 45%)" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="hsl(207 70% 45%)" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                formatter={(v, n) => [money(v), n[0].toUpperCase() + n.slice(1).replace("_", " ")]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="donations" name="Donations" stroke="#10b981" fill="url(#gDon)" strokeWidth={2} />
              <Area type="monotone" dataKey="expenses" name="Expenses" stroke="hsl(207 70% 45%)" fill="url(#gExp)" strokeWidth={2} />
              <Line type="monotone" dataKey="available_funds" name="Available Funds" stroke="hsl(45 85% 50%)" strokeWidth={2.5} dot={false} strokeDasharray="4 3" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
